"use client";
import { dateTime, t, useLocale } from "../i18n";
import { formatLastActive } from "./activity";
import { useNow } from "./use-now";
import { usePageVisible } from "./use-page-visible";
import type { Locale } from "../../shared/translations";
import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import Check from "~icons/ph/check";
import Copy from "~icons/ph/copy";
import Maximize2 from "~icons/ph/arrows-out";
import Minimize2 from "~icons/ph/arrows-in";
import Search from "~icons/ph/magnifying-glass";
import Ban from "~icons/ph/prohibit";
import Flag from "~icons/ph/flag";
import MoreHorizontal from "~icons/ph/dots-three";
import Trash from "~icons/ph/trash";
import UserMinus from "~icons/ph/user-minus";
import X from "~icons/ph/x";
import type { Person } from "../../shared/types";

// Open modals, oldest first, with the element each returns focus to. Only the
// top one is outside the inert page, so anything that must stay reachable,
// like the toast, renders inside it.
const openModals: { dialog: HTMLDialogElement; opener: Element | null }[] = [];
const modalListeners = new Set<() => void>();
export const useTopModal = () =>
  useSyncExternalStore(
    (listener) => {
      modalListeners.add(listener);
      return () => void modalListeners.delete(listener);
    },
    () => openModals.at(-1)?.dialog ?? null,
  );

// The components below that open with this directive stay on screen while the
// language changes, and t() reads a language the compiler cannot see.
export function FormError({ message }: { message: string }) {
  "use no memo";
  if (!message) return null;
  return (
    <p className="form-error" role="alert">
      {t(message)}
    </p>
  );
}

export function FriendSearch({
  value,
  onChange,
  inputId,
  label = t("البحث في الأصدقاء"),
}: {
  value: string;
  onChange: (value: string) => void;
  inputId?: string;
  label?: string;
}) {
  return (
    <label className="friend-search">
      <Search width={17} height={17} aria-hidden="true" />
      <input
        id={inputId}
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={label}
        aria-label={label}
        autoComplete="off"
        spellCheck={false}
      />
    </label>
  );
}

function LastActive({
  value,
  locale,
  compact,
}: {
  value: number;
  locale: Locale;
  compact: boolean;
}) {
  "use no memo";
  const now = useNow();
  const label = t("آخر ظهور {date}", { date: dateTime(value) });
  return (
    <time dateTime={new Date(value).toISOString()} title={label}>
      {formatLastActive(value, locale, now, compact)}
    </time>
  );
}

export function ActivityStatus({
  person,
  compact = false,
}: {
  person: Person;
  compact?: boolean;
}) {
  "use no memo";
  const locale = useLocale();
  if (person.status === "offline" && person.last_active_at != null)
    return (
      <LastActive
        value={person.last_active_at}
        locale={locale}
        compact={compact}
      />
    );
  return t(
    person.status === "online"
      ? "متصل"
      : person.status === "busy"
        ? "مشغول"
        : "غير متصل",
  );
}

// The button itself says it worked, keeping its width so nothing beside it moves.
export function CopyButton({
  className,
  text,
  label,
  iconOnly = false,
  iconSize = 16,
  onResult,
}: {
  className: string;
  text: string;
  label: string;
  iconOnly?: boolean;
  iconSize?: number;
  onResult: (copied: boolean) => void;
}) {
  "use no memo";
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      onResult(true);
    } catch {
      setCopied(false);
      onResult(false);
    }
  }
  const Icon = copied ? Check : Copy;
  const done = t("تم النسخ");
  return (
    <>
      <button
        className={className}
        aria-label={iconOnly ? (copied ? done : label) : undefined}
        title={iconOnly ? (copied ? done : label) : undefined}
        onClick={() => void copy()}
      >
        <Icon width={iconSize} height={iconSize} aria-hidden="true" />
        {!iconOnly && (
          <span className="copy-label" data-copied={copied || undefined}>
            <span>{label}</span>
            <span>{done}</span>
          </span>
        )}
      </button>
      {copied && (
        <span className="sr-only" role="status">
          {done}
        </span>
      )}
    </>
  );
}

export function Wave() {
  return (
    <span className="wave" aria-hidden="true">
      <i />
      <i />
      <i />
      <i />
      <i />
    </span>
  );
}
// Bundled with hashed names so Workers Static Assets can cache them immutably.
const avatarUrls = import.meta.glob<string>("../assets/avatars/*.svg", {
  eager: true,
  query: "?url",
  import: "default",
});
export function Avatar({
  person,
  large = false,
  dot = false,
}: {
  person: Partial<Person>;
  large?: boolean;
  dot?: boolean;
}) {
  return (
    <span
      className={`avatar ${person.avatar ? "picture" : person.color || "blue"} ${large ? "large" : ""}`}
    >
      {person.avatar ? (
        <img
          src={avatarUrls[`../assets/avatars/${person.avatar}.svg`]}
          alt=""
          draggable={false}
        />
      ) : (
        <span>
          {person.name
            ?.split(" ")
            .slice(0, 2)
            .map((s) => s[0])
            .join("") || "?"}
        </span>
      )}
      {dot && <i className={`presence ${person.status || "offline"}`} />}
    </span>
  );
}
export function ProfileAvatar({
  person,
  dot = true,
  onOpen,
}: {
  person: Person;
  dot?: boolean;
  onOpen: (person: Person) => void;
}) {
  return (
    <button
      className="avatar-button"
      aria-label={t("عرض حساب {name}", { name: person.name })}
      onClick={() => onOpen(person)}
    >
      <Avatar person={person} dot={dot} />
    </button>
  );
}
export function PersonMenu({
  person,
  isFriend,
  disabled,
  onChoose,
  onRemoveRecent,
}: {
  person: Person;
  isFriend: boolean;
  disabled: boolean;
  onChoose: (
    person: Person,
    action: "block" | "remove-friend" | "report",
  ) => void;
  onRemoveRecent?: () => void;
}) {
  const menuId = useId();
  return (
    <div className="person-menu">
      <button
        type="button"
        className="icon-button"
        popoverTarget={menuId}
        aria-label={t("خيارات {name}", { name: person.name })}
        title={t("خيارات {name}", { name: person.name })}
      >
        <MoreHorizontal width={18} height={18} />
      </button>
      <div id={menuId} popover="auto" className="person-menu-items">
        {onRemoveRecent && (
          <button
            type="button"
            disabled={disabled}
            popoverTarget={menuId}
            popoverTargetAction="hide"
            onClick={onRemoveRecent}
          >
            <Trash width={16} height={16} />
            {t("إزالة من الأخيرة")}
          </button>
        )}
        {isFriend && !onRemoveRecent && (
          <button
            type="button"
            disabled={disabled}
            popoverTarget={menuId}
            popoverTargetAction="hide"
            onClick={() => onChoose(person, "remove-friend")}
          >
            <UserMinus width={16} height={16} />
            {t("إزالة الصديق")}
          </button>
        )}
        {/* A call-link guest has no account to block. */}
        {!person.guest && (
          <button
            type="button"
            disabled={disabled}
            popoverTarget={menuId}
            popoverTargetAction="hide"
            onClick={() => onChoose(person, "block")}
          >
            <Ban width={16} height={16} />
            {t("حظر الحساب")}
          </button>
        )}
        <button
          type="button"
          disabled={disabled}
          popoverTarget={menuId}
          popoverTargetAction="hide"
          onClick={() => onChoose(person, "report")}
        >
          <Flag width={16} height={16} />
          {t("الإبلاغ")}
        </button>
      </div>
    </div>
  );
}
export function Modal({
  title,
  children,
  onClose,
  className = "",
  allowFullscreen = false,
  dismissible = true,
  closeLabel = t("إغلاق"),
  closeIcon = <X width={19} height={19} />,
}: {
  title: string;
  children: ReactNode;
  // Without onClose there is no close button; the owner removes the modal.
  onClose?: () => void;
  className?: string;
  allowFullscreen?: boolean;
  // Escape and backdrop clicks close only dismissible modals.
  dismissible?: boolean;
  closeLabel?: string;
  closeIcon?: ReactNode;
}) {
  "use no memo";
  const dialog = useRef<HTMLDialogElement>(null),
    ref = useRef<HTMLDivElement>(null);
  const [fullscreen, setFullscreen] = useState(false),
    [fullscreenError, setFullscreenError] = useState(false);
  const dismiss = () => {
    if (dismissible && onClose) onClose();
  };
  useLayoutEffect(() => {
    const element = dialog.current!,
      entry = { dialog: element, opener: element.ownerDocument.activeElement };
    element.showModal();
    // Forms whose only job is typing start in their field.
    (element.querySelector<HTMLElement>("[data-autofocus]") ?? element).focus();
    openModals.push(entry);
    modalListeners.forEach((listener) => listener());
    return () => {
      const index = openModals.indexOf(entry),
        above = openModals[index + 1];
      openModals.splice(index, 1);
      element.close();
      modalListeners.forEach((listener) => listener());
      // A modal closing under another one, such as a call under its hang-up
      // question, hands its opener up when the other's opener goes with it.
      if (above) {
        if (above.opener && element.contains(above.opener))
          above.opener = entry.opener;
      } else if (
        entry.opener &&
        "focus" in entry.opener &&
        typeof entry.opener.focus === "function"
      )
        entry.opener.focus();
    };
  }, []);
  useEffect(() => {
    const panel = ref.current;
    const update = () => setFullscreen(document.fullscreenElement === panel);
    document.addEventListener("fullscreenchange", update);
    if (!allowFullscreen && document.fullscreenElement === panel)
      void document.exitFullscreen().catch(() => {});
    return () => {
      document.removeEventListener("fullscreenchange", update);
      if (document.fullscreenElement === panel)
        void document.exitFullscreen().catch(() => {});
    };
  }, [allowFullscreen]);
  async function toggleFullscreen() {
    setFullscreenError(false);
    try {
      if (document.fullscreenElement === ref.current)
        await document.exitFullscreen();
      else await ref.current?.requestFullscreen();
    } catch {
      setFullscreenError(true);
    }
  }
  return (
    // The dialog owns Escape and clicks on its backdrop.
    // oxlint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <dialog
      ref={dialog}
      className="modal-backdrop"
      aria-label={title}
      tabIndex={-1}
      onKeyDown={(event) => {
        if (event.key !== "Escape") return;
        // Escape closes the native popover before its containing dialog.
        if (event.currentTarget.querySelector(":popover-open")) return;
        // Handled here, the key never asks the browser to close the dialog.
        event.preventDefault();
        event.stopPropagation();
        dismiss();
      }}
      onCancel={(event) => {
        event.preventDefault();
        dismiss();
      }}
      onClose={(event) => {
        // The close event arrives later, after an unmount or a remount when
        // React closed it. Otherwise a repeated back gesture closed it without
        // asking; it reopens unless it may be dismissed.
        const element = event.currentTarget;
        if (element.open || !element.isConnected) return;
        if (dismissible && onClose) onClose();
        else element.showModal();
      }}
      onMouseDown={(event) => {
        if (event.target !== event.currentTarget) return;
        event.preventDefault();
        dismiss();
      }}
    >
      <div className={`modal ${className}`} ref={ref}>
        {allowFullscreen && document.fullscreenEnabled && (
          <button
            className="icon-button fullscreen-button"
            aria-label={t(fullscreen ? "الخروج من ملء الشاشة" : "شاشة كاملة")}
            title={t(fullscreen ? "الخروج من ملء الشاشة" : "شاشة كاملة")}
            aria-pressed={fullscreen}
            onClick={() => void toggleFullscreen()}
          >
            {fullscreen ? (
              <Minimize2 width={19} height={19} />
            ) : (
              <Maximize2 width={19} height={19} />
            )}
          </button>
        )}
        {allowFullscreen && fullscreenError && (
          <p className="fullscreen-error" role="alert">
            {t("تعذر فتح ملء الشاشة.")}
          </p>
        )}
        {onClose && (
          <button
            className="icon-button close"
            onClick={onClose}
            aria-label={closeLabel}
            title={closeLabel}
          >
            {closeIcon}
          </button>
        )}
        {children}
      </div>
    </dialog>
  );
}
// Each period spans 1000 units; CSS sets its width and height in pixels, so
// the wave keeps its shape on any screen. Three harmonics give an uneven,
// hand-drawn sweep that still repeats, so the layer loops by sliding a period.
const wavePeriods = 3;
const waveStep = 25;
function waveStrand(offset: number) {
  const y = (step: number) => {
    const t = ((step * waveStep) / 500) * Math.PI;
    const sweep =
      0.62 * Math.sin(t) +
      0.28 * Math.sin(2 * t + 1.9) +
      0.1 * Math.sin(3 * t + 0.6);
    // Strands fan apart and draw together along the sweep, like silk.
    return 70 * sweep + offset * (6 + 4 * Math.sin(t + 2.4));
  };
  let d = `M 0 ${y(0).toFixed(1)}`;
  for (let step = 0; step < (wavePeriods * 1000) / waveStep; step++) {
    // Catmull-Rom through the samples, written as cubic Béziers.
    const x = step * waveStep;
    const c1 = y(step) + (y(step + 1) - y(step - 1)) / 6;
    const c2 = y(step + 1) - (y(step + 2) - y(step)) / 6;
    d += ` C ${x + waveStep / 3} ${c1.toFixed(1)} ${x + (waveStep * 2) / 3} ${c2.toFixed(1)} ${x + waveStep} ${y(step + 1).toFixed(1)}`;
  }
  return d;
}
const waveCrest = waveStrand(0);
const waveStrands = [-3, -2, -1, 0, 1, 2, 3].map(waveStrand);

export function Backdrop({ paused = false }: { paused?: boolean }) {
  const visible = usePageVisible();
  return (
    <div
      className="ambient"
      aria-hidden="true"
      data-paused={paused || !visible || undefined}
    >
      {["near", "far"].map((layer) => (
        <div className={`sound-wave sound-wave--${layer}`} key={layer}>
          <svg
            viewBox={`0 -100 ${wavePeriods * 1000} 200`}
            fill="none"
            preserveAspectRatio="none"
          >
            <path className="sound-wave-haze" d={waveCrest} />
            <g className="sound-wave-lines">
              {waveStrands.map((strand) => (
                <path d={strand} key={strand} />
              ))}
            </g>
            <path className="sound-wave-crest" d={waveCrest} />
          </svg>
        </div>
      ))}
    </div>
  );
}
